import { useState, useMemo } from 'react';
import { problems } from '../data/problems';
import StatsCard from '../components/StatsCard';
import ProblemCard from '../components/ProblemCard';

const ALL_DIFFICULTIES = ['All', 'Easy', 'Medium', 'Hard'];
const ALL_LANGUAGES = ['All', 'JavaScript', 'Java', 'SQL'];

const getAllTopics = () => {
  const topics = new Set();
  problems.forEach(p => p.topics.forEach(t => topics.add(t)));
  return ['All', ...Array.from(topics).sort()];
};

const ALL_TOPICS = getAllTopics();

const difficultyButtonStyles = {
  Easy:   { active: 'bg-green-500/20 text-green-400 border-green-500/40', inactive: 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600' },
  Medium: { active: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40', inactive: 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600' },
  Hard:   { active: 'bg-red-500/20 text-red-400 border-red-500/40', inactive: 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600' },
  All:    { active: 'bg-purple-500/20 text-purple-400 border-purple-500/40', inactive: 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600' },
};

export default function DashboardPage() {
  const [search, setSearch]       = useState('');
  const [difficulty, setDifficulty] = useState('All');
  const [language, setLanguage]   = useState('All');
  const [topic, setTopic]         = useState('All');

  const filtered = useMemo(() => {
    return problems.filter(p => {
      const matchSearch     = p.title.toLowerCase().includes(search.toLowerCase());
      const matchDifficulty = difficulty === 'All' || p.difficulty === difficulty;
      const matchLanguage   = language === 'All' || p.language === language;
      const matchTopic      = topic === 'All' || p.topics.includes(topic);
      return matchSearch && matchDifficulty && matchLanguage && matchTopic;
    });
  }, [search, difficulty, language, topic]);

  const easy   = problems.filter(p => p.difficulty === 'Easy').length;
  const medium = problems.filter(p => p.difficulty === 'Medium').length;
  const hard   = problems.filter(p => p.difficulty === 'Hard').length;
  const jsCount   = problems.filter(p => p.language === 'JavaScript').length;
  const javaCount = problems.filter(p => p.language === 'Java').length;
  const sqlCount  = problems.filter(p => p.language === 'SQL').length;

  return (
    <div className="min-h-screen bg-gray-950 pt-20 pb-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="py-8 sm:py-10">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">
            LeetCode Journey
          </h1>
          <p className="text-gray-500 text-sm">
            {problems.length} problems solved &mdash; tracking progress one problem at a time
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-4">
          <StatsCard count={easy}   label="Easy"   valueColor="text-green-400"  borderColor="border-green-900/50" />
          <StatsCard count={medium} label="Medium" valueColor="text-yellow-400" borderColor="border-yellow-900/50" />
          <StatsCard count={hard}   label="Hard"   valueColor="text-red-400"    borderColor="border-red-900/50" />
        </div>

        {/* Language summary */}
        <div className="flex items-center gap-3 text-xs text-gray-600 mb-8 px-1">
          <span className="text-purple-700 font-medium">JS {jsCount}</span>
          <span className="text-gray-700">·</span>
          <span className="text-orange-800 font-medium">Java {javaCount}</span>
          <span className="text-gray-700">·</span>
          <span className="text-blue-800 font-medium">SQL {sqlCount}</span>
        </div>

        {/* Filter panel */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4 mb-5 space-y-3">

          {/* Search */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search problems..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-gray-200 placeholder-gray-600 focus:outline-none focus:border-purple-600 text-sm transition-colors"
            />
          </div>

          {/* Difficulty + Language row */}
          <div className="flex flex-wrap gap-2">
            {ALL_DIFFICULTIES.map(d => {
              const styles = difficultyButtonStyles[d];
              const isActive = difficulty === d;
              return (
                <button
                  key={d}
                  onClick={() => setDifficulty(d)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${isActive ? styles.active : styles.inactive}`}
                >
                  {d}
                </button>
              );
            })}

            <div className="ml-auto flex gap-2">
              {ALL_LANGUAGES.map(l => (
                <button
                  key={l}
                  onClick={() => setLanguage(l)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    language === l
                      ? 'bg-purple-500/20 text-purple-400 border-purple-500/40'
                      : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600'
                  }`}
                >
                  {l === 'JavaScript' ? 'JS' : l}
                </button>
              ))}
            </div>
          </div>

          {/* Topic chips */}
          <div className="flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-none">
            {ALL_TOPICS.map(t => (
              <button
                key={t}
                onClick={() => setTopic(t)}
                className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                  topic === t
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-800 text-gray-500 hover:text-gray-400'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* Problem list */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-800 flex items-center">
            <span className="text-xs text-gray-600">
              {filtered.length} {filtered.length === 1 ? 'problem' : 'problems'}
              {filtered.length !== problems.length && ` (filtered from ${problems.length})`}
            </span>
          </div>

          {filtered.length === 0 ? (
            <div className="py-16 text-center text-gray-700 text-sm">
              No problems match your filters.
            </div>
          ) : (
            filtered.map((problem, i) => (
              <ProblemCard key={problem.id} problem={problem} index={i} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
